"use client";
import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useReportClientError } from "./reportClientError";
import NotFound from "../../components/common/NotFound";
import { getLastUserEvent } from "./global-events";
import { useAppSelector } from "../../components/slice/hooks";
import { getActiveUser } from "../../components/slice/auth-slice";

function ErrorFallback({ chunkError }) {
  if (chunkError) {
    return (
      <div style={styles.wrapper}>
        <p>We’ve updated the app. Refreshing now...</p>
      </div>
    );
  }
  return (
    <div style={styles.wrapper}>
      <NotFound />
      <button
        style={styles.button}
        onClick={() => window.location.reload(true)}
      >
        Reload Page
      </button>
      <p style={styles.info}>If the issue continues, please contact support.</p>
    </div>
  );
}

function formatLastUserEvent(event) {
  if (!event) return "N/A";

  try {
    const { type, target } = event;
    if (!target || typeof target.tagName !== "string")
      return `${type} on [unknown element]`;

    const tag = target.tagName.toLowerCase();
    const id = target.id ? `#${target.id}` : "";
    const classes = target.className
      ? `.${target.className.toString().trim().split(/\s+/).join(".")}`
      : "";

    return `${type} on ${tag}${id}${classes}`;
  } catch {
    return "Error formatting event";
  }
}

async function withTimeout(promise, ms = 2000) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Screenshot timeout")), ms)
    ),
  ]);
}

async function safeScreenshot() {
  try {
    if (typeof window !== "undefined" && document?.body) {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await withTimeout(html2canvas(document.body, { scale: 0.5 }));
      return canvas.toDataURL("image/png");
    }
  } catch (err) {
    console.warn("Screenshot capture failed:", err);
  }
  return null;
}

export default function ErrorBoundaryWrapper({ children }) {
  const reportClientError = useReportClientError();
  const [hasError, setHasError] = useState(false);
  const [chunkError, setChunkError] = useState(false);
  const { tokenData } = useAppSelector(getActiveUser);

  const getCleanElement = (el) => {
    if (!el) return null;
    if (["BODY", "HTML"].includes(el.tagName)) return null;
    return el.outerHTML?.slice(0, 300);
  };

  // Global JS error + rejection handling
  useEffect(() => {
    const handleError = async (event) => {
      try {
        const error = event?.error || event?.message || "Unknown client error";
        const isChunkError =
          error?.name === "ChunkLoadError" ||
          error?.message?.includes("Loading chunk");

        setChunkError(isChunkError);

        const metaData = {
          lastUserEvent: formatLastUserEvent(getLastUserEvent()),
          activeElement: getCleanElement(document.activeElement),
          hoveredElement: getCleanElement(document.querySelector(":hover")),
          screenshotBase64: await safeScreenshot(),
        };

        const details = {
          type: isChunkError ? "ChunkLoadError" : "ClientError",
          message: error?.toString(),
          pathname: window.location.pathname,
        };

        reportClientError(event, details, metaData, tokenData?.token);

        if (isChunkError) {
          setTimeout(() => window.location.reload(true), 1500);
        }
      } catch (_) {}
      setHasError(true);
    };

    const handleRejection = async (event) => {
      try {
        const reason = event?.reason || "Unhandled promise rejection";
        const metaData = {
          lastUserEvent: formatLastUserEvent(getLastUserEvent()),
          activeElement: getCleanElement(document.activeElement),
          hoveredElement: getCleanElement(document.querySelector(":hover")),
          screenshotBase64: await safeScreenshot(),
        };

        const details = {
          type: "UnhandledPromiseRejection",
          message: reason?.toString(),
          pathname: window.location.pathname,
        };

        reportClientError(event, details, metaData, tokenData?.token);
      } catch (_) {}
      setHasError(true);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, [reportClientError, tokenData]);

  // React render errors
  const handleReactError = async (error, info) => {
    try {
      const isChunkError =
        error?.name === "ChunkLoadError" ||
        error?.message?.includes("Loading chunk");

      setChunkError(isChunkError);

      const metaData = {
        lastUserEvent: formatLastUserEvent(getLastUserEvent()),
        activeElement: getCleanElement(document.activeElement),
        hoveredElement: getCleanElement(document.querySelector(":hover")),
        componentStack: info?.componentStack,
        screenshotBase64: await safeScreenshot(),
      };

      const details = {
        type: isChunkError ? "ChunkLoadError" : "ReactRenderError",
        message: error?.message || error?.toString(),
        pathname: window.location.pathname,
      };

      reportClientError(error, details, metaData, tokenData?.token);

      if (isChunkError) {
       setTimeout(() => location.replace(location.href), 1500);
      }
    } catch (_) {}
    setHasError(true);
  };

  if (hasError) {
    return <ErrorFallback chunkError={chunkError} />;
  }

  return (
    <ErrorBoundary
      onError={handleReactError}
      fallbackRender={({ resetErrorBoundary }) => (
        <ErrorFallback onRetry={resetErrorBoundary} />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "3rem",
    minHeight: "100vh",
    backgroundColor: "#fff0f0",
    color: "#800000",
    textAlign: "center",
  },
  button: {
    marginTop: "2rem",
    padding: "0.75rem 1.5rem",
    fontSize: "1rem",
    backgroundColor: "#800000",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  info: {
    marginTop: "1rem",
    fontSize: "0.9rem",
    color: "#444",
  },
};
